import React, { useEffect, useState } from 'react';
import { Save, RotateCcw, Loader2, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { applyTheme, useTheme } from '../ThemeProvider';

const FONT_OPTIONS = ['Cinzel', 'Inter', 'Montserrat', 'Oswald', 'Playfair', 'Poppins', 'Roboto'];
const PRESETS = [
  { name: 'Primavera Cavese (default)', primary: '#001f3f', accent: '#ec4899', secondary: '#0ea5e9' },
  { name: 'Calcio Italia', primary: '#0a2540', accent: '#22c55e', secondary: '#fbbf24' },
  { name: 'Notte Stellata', primary: '#0b0e2c', accent: '#a78bfa', secondary: '#f59e0b' },
  { name: 'Mediterraneo', primary: '#0c4a6e', accent: '#f97316', secondary: '#06b6d4' },
];

export default function ThemeManager() {
  const { theme, refresh } = useTheme();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (theme) setDraft(toDraft(theme)); }, [theme]);

  if (!draft) {
    return <div className="text-center py-10"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;
  }

  const set = (k, v) => {
    const next = { ...draft, [k]: v };
    setDraft(next);
    applyTheme(toBackend(next));
  };

  const applyPreset = (p) => {
    const next = { ...draft, color_primary: p.primary, color_accent: p.accent, color_secondary: p.secondary };
    setDraft(next);
    applyTheme(toBackend(next));
  };

  const reset = () => {
    const def = { color_primary: '#001f3f', color_accent: '#ec4899', color_secondary: '#0ea5e9', font_display: 'Cinzel', font_body: 'Inter', hero_height: 85 };
    setDraft(def);
    applyTheme(toBackend(def));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings(toBackend(draft));
      toast.success('Tema aggiornato');
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Errore di salvataggio');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="theme-manager">
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-lg flex items-center gap-2"><Palette size={18} className="text-pink-brand" /> Tema globale</h2>
          <div className="flex gap-2">
            <button onClick={reset} className="px-3 py-2 rounded-full bg-navy-dark border border-white/15 text-white/70 text-xs font-bold uppercase inline-flex items-center gap-1"><RotateCcw size={12} /> Default</button>
            <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5 !text-sm" data-testid="theme-save-button"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tema'}</button>
          </div>
        </div>
        <p className="text-white/60 text-sm">Le modifiche sono visibili in tempo reale su questa pagina. Premi <b>Salva tema</b> per renderle permanenti.</p>
      </div>

      <div className="glass-card p-5">
        <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-3">Preset rapidi</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PRESETS.map((p) => (
            <button key={p.name} onClick={() => applyPreset(p)} className="text-left p-3 rounded-xl border border-white/10 bg-navy-dark hover:border-pink-brand transition-colors" data-testid={`theme-preset-${p.name.split(' ')[0]}`}>
              <div className="text-white font-semibold text-sm mb-2">{p.name}</div>
              <div className="flex gap-1">
                <span className="w-6 h-6 rounded-full" style={{ backgroundColor: p.primary }} />
                <span className="w-6 h-6 rounded-full" style={{ backgroundColor: p.accent }} />
                <span className="w-6 h-6 rounded-full" style={{ backgroundColor: p.secondary }} />
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="glass-card p-5">
        <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-3">Colori</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <ColorField label="Primario (sfondo)" value={draft.color_primary} onChange={(v) => set('color_primary', v)} />
          <ColorField label="Accento (CTA, link)" value={draft.color_accent} onChange={(v) => set('color_accent', v)} />
          <ColorField label="Secondario" value={draft.color_secondary} onChange={(v) => set('color_secondary', v)} />
        </div>
      </div>

      <div className="glass-card p-5">
        <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-3">Tipografia</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <FontField label="Font titoli" value={draft.font_display} onChange={(v) => set('font_display', v)} testid="theme-font-display" />
          <FontField label="Font testi" value={draft.font_body} onChange={(v) => set('font_body', v)} testid="theme-font-body" />
        </div>
        <div className="mt-4 p-4 bg-navy-dark rounded-lg">
          <div style={{ fontFamily: `'${draft.font_display}', serif` }} className="text-white text-2xl font-bold uppercase">Anteprima titolo Memorial</div>
          <div style={{ fontFamily: `'${draft.font_body}', sans-serif` }} className="text-white/80 text-sm mt-2">Questo è un testo di esempio per vedere il font del corpo del sito.</div>
        </div>
      </div>

      <div className="glass-card p-5">
        <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-3">Dimensioni</h3>
        <label className="block">
          <span className="text-white/60 text-xs font-bold uppercase tracking-widest">Altezza Hero (vh): {draft.hero_height}</span>
          <input type="range" min="50" max="100" value={draft.hero_height} onChange={(e) => set('hero_height', parseInt(e.target.value, 10))} className="w-full mt-2" data-testid="theme-hero-height" />
        </label>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-2 block">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 rounded cursor-pointer border border-white/15 bg-navy-dark" />
        <input value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm font-mono" />
      </div>
    </div>
  );
}

function FontField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-2 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm" data-testid={testid}>
        {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
    </div>
  );
}

function toDraft(t) {
  return {
    color_primary: t.theme_color_primary || '#001f3f',
    color_accent: t.theme_color_accent || '#ec4899',
    color_secondary: t.theme_color_secondary || '#0ea5e9',
    font_display: t.theme_font_display || 'Cinzel',
    font_body: t.theme_font_body || 'Inter',
    hero_height: parseInt(t.theme_hero_height || 85, 10),
  };
}
function toBackend(d) {
  return {
    theme_color_primary: d.color_primary,
    theme_color_accent: d.color_accent,
    theme_color_secondary: d.color_secondary,
    theme_font_display: d.font_display,
    theme_font_body: d.font_body,
    theme_hero_height: String(d.hero_height),
  };
}
